import type { ColumnType } from '../composites/listViewer';
import { isLockedColumn } from '../composites/itemTable';
import { statusDefinitionSeed } from '../statusDefinitions';
import type { StringKey } from '../strings';
import { calendarDayInZone, hasDayShape } from '../zonedTime';
import { RefusalError } from '../failures/refusal';
import type { CatalogAttributeKind } from './attributeModal';

/**
 * The New View Modal (piece 5). It opens from the view-switcher Dropdown's
 * mandatory "Add New View" row on a List Page or a Document List Page, and from
 * the pencil on each of the person's own views as Edit View (decision 2170). It
 * writes the `SavedView` columns Application shell and navigation declares.
 */

/**
 * The stored comparator values. 🔒 They never change (decision 2172): the words
 * a person reads are labels over them, chosen by the field's type, so no saved
 * view is rewritten when a label moves.
 */
export const VIEW_COMPARATORS = ['is', 'isNot', 'contains', 'startsWith', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'] as const;
export type ViewComparator = (typeof VIEW_COMPARATORS)[number];

export interface CriteriaRow {
  id: string;
  /** The column key the row reads (decision 2173); empty until a person picks one. */
  field: string;
  comparator: ViewComparator;
  value: string;
}

export type CriteriaEvent =
  | { type: 'add'; row: CriteriaRow }
  | { type: 'remove'; id: string }
  | { type: 'set'; id: string; patch: Partial<Omit<CriteriaRow, 'id'>> };

export function criteriaReducer(rows: readonly CriteriaRow[], event: CriteriaEvent): CriteriaRow[] {
  switch (event.type) {
    case 'add':
      return [...rows, event.row];
    case 'remove':
      return rows.filter((r) => r.id !== event.id);
    case 'set':
      return rows.map((r) => (r.id === event.id ? { ...r, ...event.patch } : r));
  }
}

/** Zero criteria rows is valid and yields the "All X" default view. */
export function isAllRecordsView(rows: readonly CriteriaRow[]): boolean {
  return rows.length === 0;
}

/** Name is required; nothing else on the modal is. */
export function viewNameMissing(name: string): boolean {
  return name.trim() === '';
}

/**
 * 🔒 A name another of the person's views on the same list holds is refused at
 * the Name field (decision 2170): two views called Mine in one switcher cannot
 * be told apart. Case and outer spaces do not make a name different.
 */
export function viewNameTaken(name: string, others: readonly { id: string; name: string }[], exceptId: string | null = null): boolean {
  const wanted = name.trim().toLowerCase();
  if (wanted === '') return false;
  return others.some((view) => view.id !== exceptId && view.name.trim().toLowerCase() === wanted);
}

/**
 * 🔒 The comparators a field's type answers, in the order the select offers them
 * (decision 2172). A date never offers *contains*, and a status only is and is not.
 */
export function comparatorsForType(type: ColumnType): readonly ViewComparator[] {
  switch (type) {
    case 'number':
    case 'currency':
      return ['is', 'isNot', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'];
    case 'date':
    case 'instant':
      return ['is', 'lessThan', 'greaterThan', 'isEmpty', 'isNotEmpty'];
    case 'status':
      return ['is', 'isNot'];
    case 'image':
      return ['isEmpty', 'isNotEmpty'];
    // 🔒 A row's marks are a set of words, matched by the word a mark reads as (decision 9720).
    case 'marks':
      return ['contains', 'isEmpty', 'isNotEmpty'];
    case 'text':
    case 'link':
    case 'pills':
      return ['is', 'isNot', 'contains', 'startsWith', 'isEmpty', 'isNotEmpty'];
  }
}

/**
 * 🔒 An attribute condition on the Items list (decision 8800; T3276): its field is `attr:` and the
 * catalog attribute type's id, so a saved view keeps it by the type and never by a name that can be
 * renamed. The server narrows the Item query by it; the matcher after the read leaves it alone.
 */
export const ATTRIBUTE_CRITERION_PREFIX = 'attr:';

export function attributeCriterionField(typeId: string): string {
  return `${ATTRIBUTE_CRITERION_PREFIX}${typeId}`;
}

/** The attribute type a condition's field names, or null where the field is a column. */
export function attributeTypeIdOf(field: string): string | null {
  return field.startsWith(ATTRIBUTE_CRITERION_PREFIX) ? field.slice(ATTRIBUTE_CRITERION_PREFIX.length) : null;
}

export function isAttributeCriterion(row: Pick<CriteriaRow, 'field'>): boolean {
  return attributeTypeIdOf(row.field) !== null;
}

/**
 * 🔒 The comparators an attribute type answers (decision 8800): a Measured one compares a number in the
 * base unit, so it offers more and less than and no contains; an Enumerated one is one of its values.
 */
export function comparatorsForAttribute(kind: CatalogAttributeKind): readonly ViewComparator[] {
  return kind === 'Measured' ? ['is', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'] : ['is', 'isNot', 'isEmpty', 'isNotEmpty'];
}

/** The column type whose words an attribute's comparators read in: a Measured one's as a number's, an Enumerated one's as a status's. */
export function attributeLabelType(kind: CatalogAttributeKind): ColumnType {
  return kind === 'Measured' ? 'number' : 'status';
}

/** What joins a Measured condition's number to its unit's id in the stored value. */
const MEASURED_UNIT_JOIN = '@';

/**
 * 🔒 A Measured condition's value: the number as typed and the id of the unit it was typed in
 * (decision 8800), so a reopened view shows what the person chose. A blank number keeps the unit
 * alone, which `criteriaProblems` refuses as a blank value.
 */
export function measuredCriterionValue(entered: string, unitId: string): string {
  return `${entered.trim()}${MEASURED_UNIT_JOIN}${unitId}`;
}

/** The number and the unit's id a Measured condition's value holds as typed, either of them blank. */
export function measuredCriterionParts(value: string): { entered: string; unitId: string } {
  const at = value.lastIndexOf(MEASURED_UNIT_JOIN);
  return at < 0 ? { entered: value.trim(), unitId: '' } : { entered: value.slice(0, at).trim(), unitId: value.slice(at + 1).trim() };
}

/** The number and the unit a Measured condition's value holds, or null where it holds no number and unit. */
export function readMeasuredCriterion(value: string): { entered: number; unitId: string } | null {
  const at = value.lastIndexOf(MEASURED_UNIT_JOIN);
  if (at <= 0) return null;
  const text = value.slice(0, at).trim();
  const unitId = value.slice(at + 1).trim();
  const entered = Number(text);
  if (text === '' || unitId === '' || !Number.isFinite(entered)) return null;
  return { entered, unitId };
}

/** The words over a stored comparator, which depend on the field's type: a date's `is` reads *is on*. */
export function comparatorLabelKey(comparator: ViewComparator, type: ColumnType): StringKey {
  const dated = type === 'date' || type === 'instant';
  switch (comparator) {
    case 'is':
      return dated ? 'view.op.isOn' : 'view.op.is';
    case 'isNot':
      return 'view.op.isNot';
    case 'contains':
      return 'view.op.contains';
    case 'startsWith':
      return 'view.op.startsWith';
    case 'greaterThan':
      return dated ? 'view.op.isAfter' : 'view.op.isMoreThan';
    case 'lessThan':
      return dated ? 'view.op.isBefore' : 'view.op.isLessThan';
    case 'isEmpty':
      return 'view.op.isEmpty';
    case 'isNotEmpty':
      return 'view.op.isNotEmpty';
  }
}

/** 🔒 Is empty and is not empty draw no value box (decision 2172). */
export function comparatorTakesValue(comparator: ViewComparator): boolean {
  return comparator !== 'isEmpty' && comparator !== 'isNotEmpty';
}

/**
 * 🔒 Changing a row's field clears its value and moves its comparator to the new
 * type's first where the old one is not offered (decision 2172).
 */
export function refieldRow(row: CriteriaRow, field: string, type: ColumnType): CriteriaRow {
  const offered = comparatorsForType(type);
  return { ...row, field, value: '', comparator: offered.includes(row.comparator) ? row.comparator : offered[0]! };
}

/**
 * 🔒 A row moved onto an attribute (decisions 2172, 8800): the value clears, a Measured one keeping the
 * type's default unit, and the comparator moves to the type's first where the old one is not offered.
 */
export function refieldAttributeRow(row: CriteriaRow, field: string, kind: CatalogAttributeKind, defaultUnitId: string | null): CriteriaRow {
  const offered = comparatorsForAttribute(kind);
  return { ...row, field, value: kind === 'Measured' ? measuredCriterionValue('', defaultUnitId ?? '') : '', comparator: offered.includes(row.comparator) ? row.comparator : offered[0]! };
}

/**
 * 🔒 The Items list's Filter conditions as the address carries them (decision 8800): the rows' field,
 * comparator and value, without their ids, or nothing where no condition stands.
 */
export function formatListConditions(rows: readonly CriteriaRow[]): string {
  return rows.length === 0 ? '' : JSON.stringify(rows.map(({ field, comparator, value }) => ({ field, comparator, value })));
}

/** The conditions an address carries, each row checked as a saved view's is; text that is not a list of rows reads as none. */
export function parseListConditions(text: string | null | undefined): CriteriaRow[] {
  if (!text) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((one: unknown, index): CriteriaRow[] => {
    const row = (one ?? {}) as Record<string, unknown>;
    if (typeof row.field !== 'string' || row.field === '' || typeof row.value !== 'string' || !(VIEW_COMPARATORS as readonly unknown[]).includes(row.comparator)) return [];
    return [{ id: `condition-${index + 1}`, field: row.field, comparator: row.comparator as ViewComparator, value: row.value }];
  });
}

/** What is wrong with a row at Save: no field chosen, or a blank value where the comparator needs one. */
export type CriteriaProblem = 'field' | 'value';

/**
 * 🔒 A row whose value is blank and needs one is refused at Save, with the row
 * marked (decision 2172). A row with no field chosen is refused the same way,
 * because every condition must hold and an unpicked one says nothing.
 */
export function criteriaProblems(rows: readonly CriteriaRow[]): Readonly<Record<string, CriteriaProblem>> {
  const problems: Record<string, CriteriaProblem> = {};
  for (const row of rows) {
    if (row.field === '') problems[row.id] = 'field';
    else if (comparatorTakesValue(row.comparator) && (row.value.trim() === '' || (isAttributeCriterion(row) && row.value.trim().startsWith(MEASURED_UNIT_JOIN)))) problems[row.id] = 'value';
  }
  return problems;
}

/**
 * 🔒 A status field's value is a select of that status set's values, in the
 * set's own order (decision 2172). The members are the seeded rows, which the
 * database suite holds the table to; an enum with no rows answers none, and the
 * row then takes a text box.
 */
export function statusMembersOf(entityType: string | undefined): readonly { value: string; label: string }[] {
  if (!entityType) return [];
  return statusDefinitionSeed
    .filter((row) => row.entityType === entityType)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((row) => ({ value: row.statusValue, label: row.label }));
}

/** 🔒 The search box over Available Columns draws only where the list offers more than this many columns (decision 2173). */
export const COLUMN_SEARCH_THRESHOLD = 8;

export class ColumnPickerError extends RefusalError {}

/**
 * Removal is refused at the picker, not at save: the list's first column is
 * fixed by key and always stands (decision 2173), and an Item Table column the
 * registry locks stays locked inside this modal too — the picker is the same
 * one the Item Table hands its column set to, not a second construction with
 * its own rules.
 */
export function assertRemovable(column: string, scope: 'record' | 'itemTable', fixedColumn: string): void {
  if (column === fixedColumn) {
    throw new ColumnPickerError(`The column ${column} is the list's first, fixed in Selected Columns, and cannot be removed (decision 2173).`);
  }
  if (scope === 'itemTable' && isLockedColumn(column)) {
    throw new ColumnPickerError(`The column ${column} is locked on the Item Table, and the picker refuses to remove it (piece 4).`);
  }
}

export function canRemoveColumn(column: string, scope: 'record' | 'itemTable', fixedColumn: string): boolean {
  try {
    assertRemovable(column, scope, fixedColumn);
    return true;
  } catch {
    return false;
  }
}

/** The selected keys with the fixed column leading once, whatever order a stored view held them in. */
export function withFixedFirst(selected: readonly string[], fixedColumn: string): string[] {
  return [fixedColumn, ...selected.filter((key) => key !== fixedColumn)];
}

/**
 * 🔒 The list draws a saved view's columns in the order the view holds, the
 * fixed first column leading (decision 2173). A key the list no longer offers
 * is skipped; a view naming no columns draws the list's own set.
 */
export function viewColumns<C extends { key: string; offByDefault?: boolean }>(columns: readonly C[], keys: readonly string[] | null | undefined): C[] {
  if (!keys || keys.length === 0 || columns.length === 0) return columns.filter((column) => !column.offByDefault);
  const byKey = new Map(columns.map((column) => [column.key, column]));
  return withFixedFirst(keys, columns[0]!.key).flatMap((key) => {
    const column = byKey.get(key);
    return column ? [column] : [];
  });
}

export interface SavedViewRow {
  id: string;
  name: string;
  isFavourite: boolean;
  isDefault: boolean;
}

/**
 * Several views can be starred at once, and `isDefault` still marks the one
 * view a page opens on: starring a second view leaves the first starred and
 * moves no default.
 */
export function toggleFavourite(views: readonly SavedViewRow[], id: string): SavedViewRow[] {
  return views.map((v) => (v.id === id ? { ...v, isFavourite: !v.isFavourite } : v));
}

export interface NewViewDraft {
  name: string;
  isFavourite: boolean;
  /** Open this list on this view (decision 2171), held by one view per list and person. */
  isDefault: boolean;
  criteria: readonly CriteriaRow[];
  /** Column keys, the fixed first column leading (decision 2173). */
  selectedColumns: readonly string[];
}

export function emptyView(fixedColumn: string): NewViewDraft {
  return { name: '', isFavourite: false, isDefault: false, criteria: [], selectedColumns: [fixedColumn] };
}

/** The held value's calendar day in the zone, where it reads as a date at all. */
function heldDay(held: unknown, zone: string): string | null {
  if (held === null || held === undefined || held === '') return null;
  // A value that is already a calendar day has no instant to move: read as one,
  // it would be midnight UTC and fall on the day before west of Greenwich.
  if (typeof held === 'string' && hasDayShape(held)) return held;
  const instant = held instanceof Date ? held : new Date(String(held));
  if (Number.isNaN(instant.getTime())) return null;
  return calendarDayInZone(instant, zone);
}

export interface CriteriaMatchOptions {
  /**
   * The viewer's zone (decision 0261). 💰 A date matched by *is on* compares the
   * day in this zone, not the stored instant's text (decision 2172); a calendar
   * day stored as midnight UTC reads the same in UTC, which is the default.
   */
  zone?: string;
}

/**
 * 🔨 A saved view's criteria apply to the rows the list already holds, one
 * comparator at a time, every row of criteria having to hold (piece 12,
 * `SavedView`). A value is compared as text, case-folded, a number as a number
 * where both sides are numbers, and a day against a date as that calendar day.
 */
export function matchesCriteria(row: Record<string, unknown>, criteria: readonly CriteriaRow[], options: CriteriaMatchOptions = {}): boolean {
  const zone = options.zone ?? 'UTC';
  return criteria.every((c) => {
    // A row with no field chosen narrows nothing (decision 2172); the window refuses to save one.
    if (c.field === '') return true;
    // 🔒 An attribute condition is the Item query's to answer (decision 8800); after the read it holds for every row the query kept.
    if (isAttributeCriterion(c)) return true;
    const held = row[c.field];
    const text = held === null || held === undefined ? '' : String(held).toLowerCase();
    const wanted = c.value.trim().toLowerCase();
    const day = hasDayShape(wanted) && c.comparator !== 'contains' && c.comparator !== 'startsWith' ? heldDay(held, zone) : null;
    switch (c.comparator) {
      case 'is':
        return day !== null ? day === wanted : text === wanted;
      case 'isNot':
        return day !== null ? day !== wanted : text !== wanted;
      case 'contains':
        return text.includes(wanted);
      case 'startsWith':
        return text.startsWith(wanted);
      case 'greaterThan':
      case 'lessThan': {
        if (day !== null) return c.comparator === 'greaterThan' ? day > wanted : day < wanted;
        const a = Number(held);
        const b = Number(c.value);
        const numeric = Number.isFinite(a) && Number.isFinite(b) && c.value.trim() !== '';
        if (c.comparator === 'greaterThan') return numeric ? a > b : text > wanted;
        return numeric ? a < b : text < wanted;
      }
      case 'isEmpty':
        return text === '';
      case 'isNotEmpty':
        return text !== '';
    }
  });
}
